import { BffFacility } from "@/lib/__generated/beffe-sdk";
import { calculateDistance } from "./calculate-distance";

interface FindNearbyFacilitiesOptions {
  /** Maximum number of facilities to return */
  maxResults?: number;
  /** Maximum distance in miles to include facilities */
  maxDistanceMiles?: number;
}

/**
 * Finds nearby facilities from a list based on user's latitude and longitude.
 * Returns facilities sorted by distance (closest first).
 *
 * @param facilities - Array of facilities to search through
 * @param userLatitude - User's latitude
 * @param userLongitude - User's longitude
 * @param options - Optional configuration for filtering results
 * @returns Array of facilities with calculated distance, sorted by distance
 */
export const findNearbyFacilities = (
  facilities: BffFacility[],
  userLatitude: number,
  userLongitude: number,
  options: FindNearbyFacilitiesOptions = {},
): BffFacility[] => {
  const { maxResults, maxDistanceMiles } = options;

  // Calculate distance for each facility
  const facilitiesWithDistance: BffFacility[] = facilities
    .filter((facility) => {
      // Only include facilities that have valid location data
      return (
        facility.location?.latitude != null &&
        facility.location?.longitude != null
      );
    })
    .map((facility) => {
      const distance = calculateDistance(
        userLatitude,
        userLongitude,
        facility.location!.latitude!,
        facility.location!.longitude!,
      );

      return {
        ...facility,
        location: {
          ...facility.location,
          distance,
        },
      };
    });

  // Sort by distance (closest first)
  facilitiesWithDistance.sort(
    (a, b) => (a.location?.distance ?? 0) - (b.location?.distance ?? 0),
  );

  // Apply filters
  let result = facilitiesWithDistance;

  // Filter by max distance if specified
  if (maxDistanceMiles != null) {
    result = result.filter(
      (f) => (f.location?.distance ?? Infinity) <= maxDistanceMiles,
    );
  }

  // Limit results if specified
  if (maxResults != null) {
    result = result.slice(0, maxResults);
  }

  return result;
};

/**
 * Gets the closest facility to the user's location.
 *
 * @param facilities - Array of facilities to search through
 * @param userLatitude - User's latitude
 * @param userLongitude - User's longitude
 * @returns The closest facility with distance, or null if no facilities found
 */
export const getClosestFacility = (
  facilities: BffFacility[],
  userLatitude: number,
  userLongitude: number,
): BffFacility | null => {
  const nearby = findNearbyFacilities(facilities, userLatitude, userLongitude, {
    maxResults: 1,
  });

  return nearby.length > 0 ? nearby[0] : null;
};
